import { Box, Button, Flex, Text, Image, Skeleton, useDisclosure } from '@chakra-ui/core'
import { PostTitle } from 'components/Post/PostTitle'
import React, { useCallback, useState } from 'react'
import { PostAudioList } from '../PostAudioList'
import { UploadedFileList } from 'components/UploadedFileList'
import { PostMediaGrid } from '../PostMediaGrid'
import LocaleText from 'components/LocaleText'
import { LikeButton } from 'components/Post/LikeButton'
import { ViewButton } from 'components/Post/ViewButton'
import { CommentButton } from 'components/Post/CommentButton'
import { LikeButtonSquare } from 'components/Post/LikeButtonSquare'
import { Post } from 'redux/reducers/feed'
import { RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { FbGridVideoPreview } from 'components/CommonComponents/FbGrid/FbGridVideoPreview'
import colors from 'constants/colors'
import keys from 'constants/keys'
import { SmartAvatar } from 'components/CommonComponents/SmartAvatar'
import { useLinkedPostTasks } from 'components/Post/postHooks/useLinkedPostsTasks'
import { useLinkedChatPost } from 'components/Post/postHooks/useLinkedChatPost'
import { useLinkedKpi } from 'components/Post/postHooks/useLinkedKpi'
import { useLinkedCampaign } from 'components/Post/postHooks/useLinkedCampaign'
import { isNil } from 'lodash'
import { useLikedPosts } from 'components/Post/postHooks/useLikedPosts'
import { useViewedPosts } from 'components/Post/postHooks/useViewedPosts'
import useComments from 'hooks/useComments'
import LikesModal from 'components/Post/LikesModal'
import { ViewsModal } from 'components/Post/ViewsModal'
import PostCommentsContainer from 'components/Comments/PostCommentsContainer'

interface IProps {
  post: Post
  onInputFocusChange?: (focus: boolean) => void
  targetParentCommentId?: string | null
  targetCommentId?: string | null
}
export const PostModalBody = ({ post, onInputFocusChange, targetParentCommentId, targetCommentId }: IProps) => {
  const [showCommentsSection, setShowCommentsSection] = useState<boolean>(true)
  const [isInputFocus, setIsInputFocus] = useState<boolean>(false)

  const retailUsersObject = useSelector((state: RootState) => state.config.config.retailUsersObject)
  const commentsCounter = useSelector((state: RootState) => state.comments.commentsCounter[post.post_id])
  const uid = useSelector((state: RootState) => state.auth.uid)

  const { commentsCounterLoading } = useComments(post.post_id)

  const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onCloseLikesModal } = useDisclosure()
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onCloseViewModal } = useDisclosure()

  const { onChatClick, linkedChat, isValidChatRefId, isPrefetching } = useLinkedChatPost({ ref_id: post.ref_id })
  const { isPostLinkedToKpi, handleNavigateToKpi, currentLinkedKpi } = useLinkedKpi({ ref_id: post.ref_id })
  const { campaign, handleNavigateToCampaign, isPostLinkedToCampaign, isLoading } = useLinkedCampaign({
    ref_id: post.ref_id,
  })
  const { isValidRefId, currentLinkedTask, linkedToTaskPage, handleLinkedPost, isPostLinkedToTaskPage, taskViewNames } =
    useLinkedPostTasks({ ref_id: post.ref_id })

  const { handleUpdatedViewedPost, requireToUpdateViews } = useViewedPosts({
    post_id: post.post_id,
    created_by: post.created_by,
    groups: post.groups,
    users: post.users,
    tags: post.tags ?? [],
    viewed: post.viewed,
    myUID: uid!,
    isSearchPage: false,
    liked: post.liked,
    isDoublePost: true,
    isPersonalViewMode: true,
  })

  const { isLikedPost, likeHandler, isShowLikeButton } = useLikedPosts({
    post_id: post.post_id,
    created_by: post.created_by,
    groups: post.groups,
    tags: post.tags ?? [],
    users: post.users,
    liked: post.liked,
    myUID: uid!,
    isSearchPage: false,
    handleUpdatedViewedPost,
  })

  const handleLikesModal = useCallback(() => {
    Object.keys(post.liked).filter((key) => key !== uid!).length && onLikesModalOpen()
  }, [onLikesModalOpen, post.liked, uid])

  const showComments = useCallback(() => {
    const newShowComments = !showCommentsSection
    setShowCommentsSection(newShowComments)
    const newFocus = newShowComments
    setIsInputFocus(newFocus)
    onInputFocusChange?.(newFocus)
  }, [showCommentsSection, onInputFocusChange])

  const hasTitle = Boolean(post?.title || post?.rich_text)
  const hasAudios = Array.isArray(post.audios) && post.audios.length > 0
  const hasFiles = Array.isArray(post.files) && post.files.length > 0
  const hasImages = Array.isArray(post.images) && post.images.length > 0
  const hasVideos = Array.isArray(post.videos) && post.videos.length > 0
  const isLinkedType = post.type === keys.POST_TYPE.LINKED
  const isGroupPost = post.type !== keys.POST_TYPE.GROUP_POST

  const viewedMap = (post.viewed || {}) as Record<string, boolean>
  const isCurrentViewed = uid ? viewedMap[uid] : false

  return (
    <Box>
      {hasTitle && (
        <Flex alignItems="center">
          <Box px="25px" w="100%" wordBreak={'break-word'}>
            <PostTitle post={post} retailUsersObject={retailUsersObject} isDoublePost={false} />
          </Box>
        </Flex>
      )}
      {hasAudios && (
        <Box px="25px" py="10px">
          <PostAudioList audios={post.audios!} />
        </Box>
      )}

      {hasFiles && (
        <Box px="25px" marginBottom="15px">
          <UploadedFileList fileList={post.files!} fontSize="18px" />
        </Box>
      )}

      {hasImages && (
        <Flex mb="10px" justifyContent="center">
          <PostMediaGrid media={post.images} isEditable={false} px="25px" itemSize="250px">
            {post.images !== null &&
              post.images?.map((imageSrc) => (
                <Image key={imageSrc} src={imageSrc} width="100%" height="100%" borderRadius="10px" objectFit="cover" />
              ))}
          </PostMediaGrid>
        </Flex>
      )}

      {hasVideos && (
        <Flex mb="10px" justifyContent="center">
          <PostMediaGrid media={post.videos} isEditable={false} px="25px" itemSize="150px">
            {post.videos !== null &&
              post.videos?.map((videoSrc) => <FbGridVideoPreview key={videoSrc} src={videoSrc} />)}
          </PostMediaGrid>
        </Flex>
      )}

      <Box className="card-bottom" color={colors.greyMain} fontWeight={500} fontSize="16px">
        {isValidRefId && isLinkedType && (
          <Flex justifyContent="space-between" alignItems="center" my="10px">
            <Box>
              <Text fontSize="13px" color={colors.greyDark}>
                <LocaleText text="linked_tasks_title" />
              </Text>
              <Text color={colors.blackLight} fontWeight="bold">
                {isPostLinkedToTaskPage && !isNil(linkedToTaskPage) ? (
                  <LocaleText text={(taskViewNames as any)[linkedToTaskPage]} />
                ) : (
                  currentLinkedTask.title
                )}
              </Text>
            </Box>
            <Box>
              <Button
                onClick={handleLinkedPost}
                color={colors.blackLight}
                fontSize="12px"
                variant="outline"
                borderRadius="8px"
                borderColor={colors.blackLight}
              >
                <LocaleText text="view_task" />
              </Button>
            </Box>
          </Flex>
        )}

        {isValidChatRefId && isLinkedType && linkedChat && (
          <Flex justifyContent="space-between" alignItems="flex-end" my="10px">
            <Box>
              <Text fontSize="13px" color={colors.greyDark}>
                <LocaleText text="linked_chat_title" />
              </Text>
              <Box>
                <Flex flexDir="row" alignItems="center" color={colors.blackLight}>
                  <SmartAvatar
                    image={linkedChat.images?.[0]}
                    size="40px"
                    backgroundColor={colors.greyLight4}
                    iconColor={colors.tealLight}
                  />
                  <Text mx="10px">{linkedChat.title}</Text>
                </Flex>
              </Box>
            </Box>
            <Box>
              <Button
                isDisabled={isPrefetching || !linkedChat}
                isLoading={isPrefetching}
                onClick={onChatClick}
                color={colors.blackLight}
                fontSize="12px"
                variant="outline"
                borderRadius="8px"
                borderColor={colors.blackLight}
              >
                <LocaleText text="view_chat" />
              </Button>
            </Box>
          </Flex>
        )}
        {isPostLinkedToKpi && isLinkedType && (
          <Flex justifyContent="space-between" alignItems="flex-end" my="10px">
            <Box>
              <Text fontSize="13px" color={colors.greyDark}>
                <LocaleText text="linked_kpi_title" />
              </Text>
              <Text color={colors.blackLight} fontWeight="bold">
                <LocaleText text={currentLinkedKpi?.title || 'kpi_not_exist'} />
              </Text>
            </Box>
            <Box>
              <Button
                onClick={handleNavigateToKpi}
                color={colors.blackLight}
                isDisabled={!currentLinkedKpi}
                fontSize="12px"
                variant="outline"
                borderRadius="8px"
                borderColor={colors.blackLight}
              >
                <LocaleText text="view_kpi" />
              </Button>
            </Box>
          </Flex>
        )}

        {isPostLinkedToCampaign && isLinkedType && (
          <Flex justifyContent="space-between" alignItems="flex-end" my="10px">
            <Box>
              <Text fontSize="13px" color={colors.greyDark}>
                <LocaleText text="linked_campaign_title" />
              </Text>
              <Text color={colors.blackLight} fontWeight="bold">
                {isLoading ? (
                  <Skeleton height="20px" width="100px" />
                ) : (
                  <LocaleText text={campaign?.title || 'campaign_not_exist'} />
                )}
              </Text>
            </Box>
            <Box>
              <Button
                onClick={handleNavigateToCampaign}
                color={colors.blackLight}
                isDisabled={!campaign || isLoading}
                isLoading={isLoading}
                fontSize="12px"
                variant="outline"
                borderRadius="8px"
                borderColor={colors.blackLight}
              >
                <LocaleText text="view_campaign" />
              </Button>
            </Box>
          </Flex>
        )}

        {isGroupPost ? (
          <Flex
            alignItems="center"
            style={{ columnGap: '20px' }}
            justifyContent="flex-end"
            w="100%"
            px="25px"
            mt="20px"
          >
            {!isPostLinkedToCampaign && (
              <LikeButton
                created_by={post.created_by}
                likes_counter={post.likes_counter}
                liked={post.liked}
                isShowLikeButton={isShowLikeButton}
                isLikedPost={isLikedPost}
                isDoublePost={false}
                isAllPostCollapsed={false}
                handleLikesModal={handleLikesModal}
              />
            )}

            {post.viewed !== undefined && post.views_counter !== undefined && (
              <ViewButton
                isDoublePost={false}
                isAllPostCollapsed={false}
                viewed={post.viewed}
                views_counter={post.views_counter}
                created_by={post.created_by}
                openModal={onViewModalOpen}
                isShowViewIcon={requireToUpdateViews.includes(uid!) && post.created_by !== uid}
                isViewed={requireToUpdateViews.includes(uid!) && post.viewed[uid!]}
              />
            )}

            <CommentButton
              isDoublePost={false}
              isAllPostCollapsed={false}
              postId={post.post_id}
              commentsCounterLoading={commentsCounterLoading}
              showComments={showComments}
              showCommentsSection={showCommentsSection}
              commentsCounter={commentsCounter}
              isViewed={
                isCurrentViewed ||
                (requireToUpdateViews.includes(uid!) && post.created_by === uid!) ||
                !requireToUpdateViews.includes(uid!)
              }
            />
          </Flex>
        ) : (
          <Box h="25px" />
        )}
      </Box>
      {isShowLikeButton && post.type !== keys.POST_TYPE.GROUP_POST && !isPostLinkedToCampaign && (
        <LikeButtonSquare liked={post.liked[uid!]} likeHandler={likeHandler} />
      )}
      <Box px="25px">
        <PostCommentsContainer
          postID={post.post_id}
          postAuthorID={post.created_by}
          rasID={post.ras_id}
          isInputFocus={isInputFocus}
          showCommentsSection={showCommentsSection}
          updatedCommentsCounter={commentsCounter}
          isDoublePost={false}
          postType={post.type}
          hideInput={true}
          targetParentCommentId={targetParentCommentId}
          targetCommentId={targetCommentId}
        />
      </Box>
      {isLikesModalOpen && <LikesModal isOpen={isLikesModalOpen} onClose={onCloseLikesModal} post={post} {...post} />}
      {isViewModalOpen && <ViewsModal isOpen={isViewModalOpen} onClose={onCloseViewModal} {...post} />}
    </Box>
  )
}
